import React from 'react'
import i18n from 'i18next'
import { create } from 'zustand'

import { useAuth0 } from '@auth0/auth0-react'
import { singleFlightLogout } from '@core/auth/single-flight-logout'
import env from '@core/env'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { client } from './generated/client.gen'
import * as generatedService from './generated/sdk.gen'

type ApiError = {
  message: string
  status?: number
  errorCode?: string
}

const getStringProperty = (value: unknown, key: string): string | undefined => {
  if (typeof value !== 'object' || value === null || !(key in value)) return undefined

  const result = (value as Record<string, unknown>)[key]
  return typeof result === 'string' ? result : undefined
}

export const normalizeApiError = (error: unknown, response?: Response): ApiError => {
  const message =
    typeof error === 'string' ? error : (getStringProperty(error, 'message') ?? 'Unknown error')
  const errorCode = getStringProperty(error, 'errorCode') ?? getStringProperty(error, 'error_code')

  return { message, status: response?.status, errorCode }
}

export const networkErrorInterceptor = (
  error: unknown,
  response: Response | undefined,
): ApiError => {
  if (response === undefined) {
    const message =
      typeof error === 'object' && error !== null && 'message' in error
        ? String((error as { message: unknown }).message)
        : 'Network request failed'
    const networkError = new Error(message, { cause: error })
    networkError.name = 'NetworkError'
    throw networkError
  }
  return normalizeApiError(error, response)
}

const getErrorStatus = (error: unknown): number | undefined =>
  typeof error === 'object' && error !== null && 'status' in error
    ? (error as ApiError).status
    : undefined

export const isNotFoundError = (error: unknown): boolean => {
  const status = getErrorStatus(error)
  if (status === 404) return true
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string' &&
    (error as { message: string }).message.endsWith('not found')
  )
}

export const isPermissionError = (error: unknown): boolean => {
  const status = getErrorStatus(error)
  if (status === 403) return true
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string' &&
    (error as { message: string }).message.toLowerCase().includes('no permission')
  )
}

export class ItemFetchError extends Error {
  status: number

  identifier: string

  constructor(status: number, identifier: string) {
    super(`Item fetch failed: ${status}`)
    this.status = status
    this.identifier = identifier
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (isNotFoundError(error) || isPermissionError(error)) {
          return false
        }
        if (error instanceof ItemFetchError && (error.status === 403 || error.status === 404)) {
          return false
        }

        return failureCount < 3
      },
    },
  },
})

i18n.on('languageChanged', () => {
  queryClient.resetQueries()
})

export const ApiProvider = ({ children }: { children: React.ReactNode }) => {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}

const buildSessionExpiredReturnTo = () => {
  const qs = new URLSearchParams({
    returnTo: encodeURIComponent(`${window.location.pathname}${window.location.search}`),
    auth0ErrorParam: 'expired',
  })

  return `${window.document.location.origin}?${qs.toString()}`
}

const service = generatedService

type DefaultService = typeof generatedService

type ApiState = {
  client: DefaultService
}

export const useApiState = create<ApiState>((set) => {
  return {
    client: service,

    reset() {
      set({ client: service })
    },
  }
})

export const BffClientProvider = (props: {
  children: React.ReactNode
  client?: DefaultService
}) => {
  const { getAccessTokenSilently, logout } = useAuth0()

  React.useEffect(() => {
    const copy = props.client || service

    client.setConfig({ baseUrl: env.FTG_BFF_URL })

    client.interceptors.request.use(async (request) => {
      try {
        const token = await getAccessTokenSilently()
        request.headers.set('Authorization', `Bearer ${token}`)
      } catch (tokenError) {
        // Silent refresh failed (dead/revoked refresh token): the session is
        // already gone, so drive the secure logout now rather than sending a
        // doomed request to learn the same thing. Re-throw to abort this request;
        // AuthErrorBoundary shows the loader while the full-page logout redirect
        // tears the SPA down (single-flight dedupes with the 401 handler below).
        singleFlightLogout(logout, { logoutParams: { returnTo: buildSessionExpiredReturnTo() } })
        throw tokenError
      }
      return request
    })

    client.interceptors.response.use(async (response, request) => {
      if (response.status === 401) {
        singleFlightLogout(logout, { logoutParams: { returnTo: buildSessionExpiredReturnTo() } })
      } else if (response.status > 401) {
        // Suppress expected 403/404s from item-by-identifier (handled by error pages)
        const isExpectedItemError =
          (response.status === 404 || response.status === 403) &&
          request.url.includes('item-by-identifier')
        if (!isExpectedItemError) {
          // fetch does not allow parsing 2 times the response
          const body = await response.clone().text()
          // Suppress the expected non-admin organize 403 (restrict_folder_organize gate)
          const isExpectedOrganizeRestriction =
            response.status === 403 && body.includes('folders.errors.organize_forbidden')
          if (!isExpectedOrganizeRestriction) {
            console.warn('ui: request error', request.method, request.url, response.status, body)
          }
        }
      }

      return response
    })

    client.interceptors.error.use(networkErrorInterceptor)

    useApiState.setState({ client: copy })
  }, [props.client, getAccessTokenSilently, logout])

  return props.children
}

export const useApi = () => {
  return useApiState((state) => state.client)
}

export const getApi = () => {
  return useApiState.getState().client
}

export const getQueryClient = () => queryClient
